import _ from 'lodash'
import React from 'react'

import {
  ADMINISTRATION_STATUSES,
  ADMINISTRATION_STATUS_LABELS,
  ADMINISTRATION_TYPE,
} from 'services/administrations/constants'
import { Child } from 'services/child/constants'
import { DiaryRecord } from 'services/dailyDiaryMedications/models'
import { MEDICATION_FORM_STATUSES } from 'services/medicationForms/constants'

import { generateRoute } from 'utils/routing'

import { Banner, Button, DropdownMenu, Hyperlink, Table, TimeString } from 'components'

import i18n from 'translations'

import { AdministerParams } from '../../MedicationsContainer'
import { StyledTableContainer } from '../../MedicationsStyled'

interface MedicationListProps {
  child?: Child
  isCurrentDate: boolean
  onAdministerClick?: (params?: AdministerParams) => void
  onParentSignatureClick: (id: number) => void
  record?: DiaryRecord[]
}

const MedicationList: React.FC<MedicationListProps> = ({
  child,
  isCurrentDate,
  onAdministerClick,
  onParentSignatureClick,
  record,
}) => {
  const renderActionButton = (status, administration) => {
    if (status === ADMINISTRATION_STATUSES.SCHEDULED || (isCurrentDate && status === ADMINISTRATION_STATUSES.MISSED)) {
      return (
        <Button
          hierarchy="tertiary"
          label={i18n.t('module:DailyDiary:Medication:Table:ActionButtons:administer')}
          size="small"
          negativeVerticalMargins
          onClick={() => onAdministerClick({ administration, child })}
        />
      )
    }

    if (status === ADMINISTRATION_STATUSES.ADMINISTERED) {
      return (
        <Button
          hierarchy="tertiary"
          label={i18n.t('module:DailyDiary:Medication:Table:ActionButtons:parentSignature')}
          size="small"
          negativeVerticalMargins
          onClick={() => onParentSignatureClick(administration?.id)}
        />
      )
    }

    return ADMINISTRATION_STATUS_LABELS[status]
  }

  return (
    <StyledTableContainer>
      <Table visualType="transparent" noMargin>
        <Table.Thead>
          <Table.Tr>
            <Table.Th align="left" width="50%">
              {i18n.t('module:DailyDiary:Medication:Table:medication')}
            </Table.Th>
            <Table.Th align="left" width="8%">
              {i18n.t('module:DailyDiary:Medication:Table:dose')}
            </Table.Th>
            <Table.Th align="left" width="8%">
              {i18n.t('global:Time')}
            </Table.Th>
            <Table.Th align="right" width="20%" />
            <Table.Th align="right" width="8%" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {_.map(record, ({ administration }) => {
            const {
              id: administrationId,
              medicationDetails,
              medicationForm,
              status,
              takenAt,
              timeOfAdministration,
              type,
            } = administration || {}
            const { id, medicationDetails: medicationFormDetails, status: medicationFormStatus } = medicationForm || {}
            const { dose, medication } = medicationDetails || medicationFormDetails || {}

            const isMedicationExpired = medicationFormStatus === MEDICATION_FORM_STATUSES.EXPIRED

            return (
              <React.Fragment key={administrationId}>
                <Table.Tr>
                  <Table.Td align="left" noBorder={isMedicationExpired}>
                    {medication}
                  </Table.Td>
                  <Table.Td align="left" noBorder={isMedicationExpired}>
                    {dose}
                  </Table.Td>
                  <Table.Td align="left" noBorder={isMedicationExpired}>
                    {timeOfAdministration
                      ? <TimeString date={timeOfAdministration} local={false} />
                      : <TimeString date={takenAt as Date} />}
                  </Table.Td>
                  <Table.Td align="right" noBorder={isMedicationExpired}>
                    {renderActionButton(status, administration)}
                  </Table.Td>
                  <Table.Td align="right" noBorder={isMedicationExpired}>
                    {ADMINISTRATION_STATUSES.SCHEDULED !== status && (
                      <DropdownMenu minWidth={280} small>
                        {type === ADMINISTRATION_TYPE.SCHEDULED && (
                          <DropdownMenu.Item
                            icon="medication"
                            label={i18n.t('module:DailyDiary:Medication:Table:DropdownMenu:medicationForm')}
                            to={generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', {
                              childId: child?.id,
                              id,
                            })}
                          />
                        )}
                        <DropdownMenu.Item
                          icon="administration"
                          label={i18n.t('module:DailyDiary:Medication:Table:DropdownMenu:administrationDetails')}
                          to={generateRoute('CHILDREN.CHILD.SAFEGUARDING.ADMINISTRATION.PREVIEW', {
                            childId: child?.id,
                            id: administrationId,
                          })}
                        />
                      </DropdownMenu>
                    )}
                  </Table.Td>
                </Table.Tr>
                {isMedicationExpired && (
                  <Table.Tr className="medication_expired">
                    <Table.Td align="left" colSpan={5}>
                      <Banner.Error noBottomMargin noLeftBorder>
                        {i18n.t('module:DailyDiary:Medication:Table:expiredMessage', {
                          components: [
                            <Hyperlink
                              target="_blank"
                              to={generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', {
                                childId: child?.id,
                                id,
                              })}
                              variant="span"
                              underline
                            />,
                          ],
                          medication,
                        })}
                      </Banner.Error>
                    </Table.Td>
                  </Table.Tr>
                )}
              </React.Fragment>
            )
          })}
        </Table.Tbody>
      </Table>
    </StyledTableContainer>
  )
}

export default MedicationList
